import { useRef, useMemo, useState } from 'react'
import { Modal } from 'components/UI/Modal/Modal'
import { ClientDataTable } from 'components/ClientDataTable/ClientDataTable'
import confirm from 'components/dialogConfirm'
import { autoMatchWithConflictResolution } from './auto-match'
import { HeaderRow, ImportData } from './importer-types'
import { columnOptions, getColumnOption } from './ImporterViewModel'
import { ColumnDef } from '@tanstack/react-table'
import { BaseCheckbox } from 'components/FormUtils/Checkbox/checkbox'
import ReactSelectWrapper from 'components/FormUtils/ReactSelectWrapper'
import { cn } from 'components/UI/cn'
import { Building2, Search, Upload } from 'lucide-react'

// v2-specific auto-matching patterns (superset of v1 with deal/gpo fields)
const AUTO_MATCH_PATTERNS: Record<string, string[]> = {
  // Primary Keys
  company_opp_type: [
    'company_type',
    'company type',
    'business_type',
    'type',
    'category',
    'segment',
    'opp_type',
    'company_category',
    'company_segment',
  ],
  firstbite_id: ['firstbite', 'fb_id', 'chain_id', 'company_id'],

  // Company Match Fields
  company_name: [
    'company_name',
    'company',
    'business_name',
    'business',
    'restaurant_name',
    'restaurant',
    'chain_name',
    'chain',
    'name',
  ],
  company_phone: [
    'company_phone',
    'business_phone',
    'phone',
    'telephone',
    'tel',
    'phone_number',
  ],
  company_domain: [
    'company_domain',
    'domain',
    'website',
    'url',
    'web_address',
    'site',
  ],
  company_address_street: [
    'company_address',
    'business_address',
    'address',
    'street_address',
    'street',
    'addr',
  ],
  company_address_street_2: [
    'address_2',
    'address2',
    'suite',
    'unit',
    'apt',
    'apartment',
  ],
  company_address_city: ['company_city', 'business_city', 'city', 'town'],
  company_address_state: [
    'company_state',
    'business_state',
    'state',
    'province',
    'region',
  ],
  company_address_zip: [
    'company_zip',
    'business_zip',
    'zip_code',
    'zip',
    'postal_code',
    'postal',
    'postcode',
  ],
  company_address_country: [
    'company_country',
    'business_country',
    'country',
    'nation',
  ],
  company_full_address: ['full_address', 'complete_address', 'mailing_address'],

  // Contact Fields
  contact_first_name: ['first_name', 'first', 'fname', 'given_name', 'given'],
  contact_last_name: [
    'last_name',
    'last',
    'lname',
    'surname',
    'family_name',
    'family',
  ],
  contact_full_name: ['full_name', 'contact_name', 'name'],
  contact_phone: ['contact_phone', 'phone', 'mobile', 'cell', 'telephone'],
  contact_work_email: ['work_email', 'business_email', 'email', 'work_mail'],
  contact_personal_email: ['personal_email', 'private_email', 'personal_mail'],
  contact_generic_email: ['generic_email', 'info_email', 'general_email'],
  contact_title: [
    'contact_title',
    'job_title',
    'title',
    'position',
    'role',
    'designation',
  ],
  contact_linkedin_url: [
    'linkedin',
    'li_url',
    'linkedin_url',
    'linkedin_profile',
  ],
  contact_label: ['contact_label', 'label', 'contact_type'],

  // Deal Fields
  deal_sales_stage: ['stage', 'sales_stage', 'pipeline', 'deal_stage'],
  deal_account_owner: [
    'owner',
    'account_owner',
    'sales_rep',
    'owner_email',
    'account_owner_email',
    'rep_email',
  ],
  deal_product: ['product', 'service', 'offering', 'deal_product'],
  deal_product_variant: [
    'variant',
    'product_variant',
    'deal_variant',
    'variant_name',
    'deal_product_variant',
  ],
  deal_tag_name: ['deal_tag', 'deal_tags', 'deal tag', 'deal_tag_name'],
  deal_monthly_revenue_override: [
    'revenue_override',
    'monthly_revenue_override',
    'revenue',
    'monthly_revenue',
    'deal_revenue',
  ],
  deal_monthly_volume_override: [
    'volume_override',
    'monthly_volume_override',
    'volume',
    'monthly_volume',
    'deal_volume',
  ],
  deal_note: ['deal_note', 'deal_notes', 'deal note', 'deal_note_content'],

  // Other
  tag_name: ['tag', 'tags', 'company tag', 'company_tag'],
  gpo_name: [
    'gpo',
    'gpos',
    'gpo_name',
    'group purchasing',
    'group_purchasing_organization',
  ],
  company_notes: [
    'company_notes',
    'notes',
    'comments',
    'remarks',
    'description',
  ],
}

/** Build auto-matched headers from raw import headers. */
function buildAutoMatchedHeaders(headers: HeaderRow[]): HeaderRow[] {
  return autoMatchWithConflictResolution(
    headers.map((h) => ({ ...h })),
    AUTO_MATCH_PATTERNS
  )
}

export interface ImporterColumnAssignModalProps {
  data: ImportData | null
  open: boolean
  onClose: () => void
  onSave: (updatedData: ImportData, skipMatching: boolean) => void
}

export function ImporterColumnAssignModal({
  data,
  open,
  onClose,
  onSave,
}: ImporterColumnAssignModalProps) {
  const [localHeaders, setLocalHeaders] = useState<HeaderRow[]>(() => {
    if (data?.headers) {
      return buildAutoMatchedHeaders(data.headers)
    }
    return []
  })
  const [skipMatching, setSkipMatching] = useState<boolean | null>(null)

  // Reinitialize when data reference changes (new file uploaded)
  const prevDataRef = useRef(data)
  if (data !== prevDataRef.current) {
    prevDataRef.current = data
    setLocalHeaders(data?.headers ? buildAutoMatchedHeaders(data.headers) : [])
  }

  const hasChanges = useMemo(() => {
    return localHeaders.some(
      (localHeader, index) =>
        localHeader.mapped !== data?.headers[index]?.mapped
    )
  }, [localHeaders, data?.headers])

  const hasCompanyNameMapped = useMemo(() => {
    return localHeaders.some((header) => header.mapped === 'company_name')
  }, [localHeaders])

  const isSkipMatchingValid = useMemo(() => {
    if (skipMatching === null) return false
    if (skipMatching && !hasCompanyNameMapped) return false
    return true
  }, [skipMatching, hasCompanyNameMapped])

  const hasOppTypeMapped = useMemo(() => {
    return localHeaders.some((header) => header.mapped === 'company_opp_type')
  }, [localHeaders])

  const companyMatchColumns = useMemo(() => {
    return localHeaders
      .filter(
        (header) =>
          getColumnOption(header.mapped)?.type === 'company_match' ||
          getColumnOption(header.mapped)?.type === 'primary_key'
      )
      .map((header) => header.original)
  }, [localHeaders])

  const updateLocalMap = (original_header: string, mapped_column: string) => {
    setLocalHeaders((prev) =>
      prev.map((header) =>
        header.original === original_header
          ? { ...header, mapped: mapped_column }
          : header
      )
    )
  }

  const updateExclude = (original_header: string, exclude: boolean) => {
    setLocalHeaders((prev) =>
      prev.map((header) =>
        header.original === original_header
          ? {
              ...header,
              mapped: exclude
                ? 'ignore'
                : header.mapped === 'ignore'
                  ? ''
                  : header.mapped,
            }
          : header
      )
    )
  }

  const handleSave = () => {
    if (skipMatching === null || !data) return
    const updatedData: ImportData = {
      ...data,
      headers: localHeaders,
    }
    onSave(updatedData, skipMatching)
  }

  const handleCancel = () => {
    if (data?.headers) {
      setLocalHeaders(buildAutoMatchedHeaders(data.headers))
    }
    setSkipMatching(null)
    onClose()
  }

  // Track which columns are already mapped to prevent duplicates
  const mappedColumns = useMemo(() => {
    return localHeaders
      .map((h) => h.mapped)
      .filter((mapped) => mapped !== 'ignore') // Allow multiple 'ignore' mappings
  }, [localHeaders])

  // Check for contact name column conflict
  const hasContactNameConflict = useMemo(() => {
    const hasFullName = mappedColumns.includes('contact_full_name')
    const hasFirstName = mappedColumns.includes('contact_first_name')
    const hasLastName = mappedColumns.includes('contact_last_name')
    return hasFullName && (hasFirstName || hasLastName)
  }, [mappedColumns])

  const isValid = useMemo(() => {
    const allMapped = localHeaders.every(
      (header) =>
        header.mapped === 'ignore' ||
        (header.mapped && header.mapped.trim() !== '')
    )
    return allMapped && !hasContactNameConflict
  }, [localHeaders, hasContactNameConflict])

  // Create options for the select
  const selectOptions = useMemo(() => {
    const hasFullName = mappedColumns.includes('contact_full_name')
    const hasFirstOrLastName =
      mappedColumns.includes('contact_first_name') ||
      mappedColumns.includes('contact_last_name')

    return Object.entries(columnOptions).map(([k, v]) => {
      // Disable contact_full_name if first_name or last_name is already mapped
      const isFullNameDisabledByConflict =
        k === 'contact_full_name' && hasFirstOrLastName
      // Disable contact_first_name or contact_last_name if full_name is already mapped
      const isPartialNameDisabledByConflict =
        (k === 'contact_first_name' || k === 'contact_last_name') && hasFullName

      return {
        label: v.name,
        value: k,
        // Hide if already mapped by another header (but allow company_notes to be reused)
        // Also disable conflicting contact name columns
        isDisabled:
          (mappedColumns.includes(k) && k !== 'company_notes') ||
          isFullNameDisabledByConflict ||
          isPartialNameDisabledByConflict,
      }
    })
  }, [mappedColumns])

  // Refs for values accessed inside stable column cell renderers.
  // Cells re-render when table data (localHeaders) changes; refs give them
  // the latest derived values without forcing column definition churn.
  const selectOptionsRef = useRef(selectOptions)
  selectOptionsRef.current = selectOptions
  const companyMatchColumnsRef = useRef(companyMatchColumns)
  companyMatchColumnsRef.current = companyMatchColumns

  const containerStyle = useMemo(
    () => ({
      container: (provided: Record<string, unknown>) => ({
        ...provided,
        width: '100%',
      }),
    }),
    []
  )

  const columns: ColumnDef<HeaderRow>[] = useMemo(
    () => [
      {
        header: 'Header Name',
        accessorKey: 'original',
        size: 250,
        cell: (info) => {
          const header = info.row.original
          const wasAutoMatched =
            data?.headers.find((h) => h.original === header.original)
              ?.mapped !== header.mapped
          const isCompanyColumn = companyMatchColumnsRef.current.includes(
            header.original
          )
          const columnType = getColumnOption(header.mapped)?.type

          return (
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-medium text-sm truncate">
                  {header.label || header.original}
                </div>
                {wasAutoMatched && header.mapped !== 'ignore' && (
                  <div className="px-1.5 py-0.5 bg-info-bg text-info text-xs rounded shrink-0">
                    Auto-matched
                  </div>
                )}
                {isCompanyColumn && (
                  <div className="flex items-center gap-1 px-1.5 py-0.5 bg-surface-hover text-brand text-xs rounded shrink-0">
                    <Building2 size={12} />
                    <span>Company</span>
                  </div>
                )}
              </div>
              {header.label && (
                <div className="text-xs text-fg-secondary truncate">
                  {header.original}
                </div>
              )}
              {columnType && columnType !== 'ignore' && (
                <div className="text-xs text-fg-tertiary mt-0.5">
                  {getColumnOption(header.mapped)?.name}
                </div>
              )}
            </div>
          )
        },
      },
      {
        header: 'Mapped Column',
        accessorKey: 'mapped',
        size: 300,
        cell: (info) => {
          const header = info.row.original
          const isExcluded = header.mapped === 'ignore'
          const currentOptions = selectOptionsRef.current
          const selectedOption = currentOptions.find(
            (opt) => opt.value === header.mapped
          )

          return (
            <ReactSelectWrapper
              value={selectedOption || null}
              onChange={(option) => {
                const selected = option as { value: string } | null
                updateLocalMap(header.original, selected?.value || '')
              }}
              options={currentOptions}
              placeholder="Select a Column"
              isClearable
              isDisabled={isExcluded}
              styles={containerStyle}
            />
          )
        },
      },
      {
        header: 'Exclude',
        accessorKey: 'exclude',
        size: 80,
        cell: (info) => {
          const header = info.row.original
          const isExcluded = header.mapped === 'ignore'

          return (
            <div className="flex items-center justify-center h-full">
              <BaseCheckbox
                name={`exclude_${header.original}`}
                value={isExcluded}
                onChange={(exclude: boolean) =>
                  updateExclude(header.original, exclude)
                }
                style={{ justifyContent: 'center' }}
              />
            </div>
          )
        },
      },
    ],
    [data?.headers, containerStyle]
  )

  return (
    <Modal
      open={open}
      onOpenChange={async (isOpen) => {
        if (!isOpen) {
          if (hasChanges) {
            const confirmed = await confirm(
              'You have unsaved changes. Are you sure you want to close?',
              'Discard Changes',
              '',
              { confirmLabel: 'Discard', buttonVariant: 'danger' }
            )
            if (confirmed) {
              handleCancel()
            }
          } else {
            onClose()
          }
        }
      }}
      title="Update Header Mapping"
      description={
        <>
          Configure how your CSV headers map to system fields. Headers have been
          automatically matched where possible - review and adjust as needed.
        </>
      }
      footerChildren={
        <>
          {hasContactNameConflict && (
            <div
              role="alert"
              className="mt-2 p-2 bg-error-bg border border-error-border rounded text-error text-sm"
            >
              Contact Full Name cannot be used together with Contact First Name
              or Contact Last Name. Please use one or the other.
            </div>
          )}
          {!isValid && !hasContactNameConflict && (
            <div
              role="alert"
              className="mt-2 p-2 bg-error-bg border border-error-border rounded text-error text-sm"
            >
              All headers must either be mapped to a column or excluded.
            </div>
          )}
        </>
      }
      size="lg"
      acceptButtonText="Save Changes"
      onAccept={handleSave}
      onCancel={handleCancel}
      blockAccept={!hasChanges || !isValid || !isSkipMatchingValid}
    >
      {/* Company Matching Mode */}
      <div className="mb-4">
        <p className="text-sm font-medium text-fg mb-2">
          How should companies be handled?
        </p>
        <div
          role="radiogroup"
          aria-label="Company matching mode"
          className="grid grid-cols-1 sm:grid-cols-2 gap-3"
        >
          <button
            type="button"
            role="radio"
            aria-checked={skipMatching === false}
            onClick={() => setSkipMatching(false)}
            className={cn(
              'flex flex-col items-start gap-2 rounded-lg border-2 p-4 text-left transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-focus focus-visible:ring-offset-2',
              skipMatching === false
                ? 'bg-surface-hover border-brand-active text-fg'
                : 'bg-surface border-line text-fg-secondary hover:border-fg-secondary'
            )}
          >
            <Search className="w-5 h-5" />
            <span className="font-semibold text-sm">
              Match against First Bite database
            </span>
            <span className="text-xs leading-relaxed">
              Companies will be matched to existing records using name, address,
              and other mapped fields.
            </span>
          </button>

          <button
            type="button"
            role="radio"
            aria-checked={skipMatching === true}
            onClick={() => setSkipMatching(true)}
            className={cn(
              'flex flex-col items-start gap-2 rounded-lg border-2 p-4 text-left transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-line-focus focus-visible:ring-offset-2',
              skipMatching === true
                ? 'bg-surface-hover border-brand-active text-fg'
                : 'bg-surface border-line text-fg-secondary hover:border-fg-secondary'
            )}
          >
            <Upload className="w-5 h-5" />
            <span className="font-semibold text-sm">
              Upload all as custom companies
            </span>
            <span className="text-xs leading-relaxed">
              All companies will be imported as custom (user-generated) records.
              No matching will be performed.
            </span>
          </button>
        </div>

        {skipMatching === true && !hasCompanyNameMapped && (
          <div
            role="alert"
            className="mt-2 p-2 bg-error-bg border border-error-border rounded text-error text-sm"
          >
            <strong>Required:</strong> You must map at least the &quot;Company
            Name&quot; column to create custom companies.
          </div>
        )}

        {skipMatching === null && (
          <p className="mt-2 text-xs text-fg-secondary">
            Please select a company handling mode before saving.
          </p>
        )}
      </div>

      {/* Legend */}
      <div className="mb-3 flex items-center gap-4 text-sm text-fg-secondary">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-surface-hover rounded"></div>
          <span>Company column (used for matching/custom creation)</span>
        </div>
      </div>

      {hasOppTypeMapped && (
        <div className="mb-3 p-2 bg-info-bg border border-info/30 rounded text-info text-sm">
          <strong>Company Type mapped:</strong> This column restricts company
          matching to a specific type (e.g., Restaurant, University, Hospital).
          Rows with unrecognized types will match against all company types.
        </div>
      )}

      <ClientDataTable
        data={localHeaders}
        columns={columns}
        height="min(500px, 50vh)"
        enableSorting={false}
      />
    </Modal>
  )
}
